import DefaultTimeout from '../constants/Fetch.js';
import HttpHeaders from '../constants/HttpHeaders.js';
import HttpMethods from '../constants/HttpMethods.js';
import FetchError from '../errors/FetchError.js';
import { getResponseBody, isJson } from '../utils/response.js';
import BaseComponent from './BaseComponent.js';
import NillaInfo from './InfoDialog.js';

/**
 * Enum for identifiers to query DOM elements.
 * @readonly
 * @enum {string}
 */
const Elements = Object.freeze({
    Target: 'target',
    LoadingIndicator: 'loading-indicator',
    InfoDialog: 'info-dialog',
});

/**
 * Web component for pushState + ajax support.
 */
class PJax extends BaseComponent {
    /**
     * Version number to include with requests.
     * @type {string}
     */
    #version = '';

    /**
     * List of file types to ignore when intercepting links.
     * @type {string[]}
     */
    #ignoreFileTypes = ['pdf', 'doc', 'docx', 'zip', 'rar', '7z', 'gif', 'jpeg', 'jpg', 'png'];

    /**
     * Name of attribute that indicates an element should be ignored.
     * @type {string}
     */
    #excludeAttribute = 'data-pjax-no-follow';

    /**
     * Bound popState event listener.
     * @type {(event: PopStateEvent) => Promise<void>|undefined}
     */
    #popStateListener;

    /**
     * Bound click event listener.
     * @type {(event: MouseEvent) => Promise<void>|undefined}
     */
    #clickListener;

    /**
     * Bound submit event listener.
     * @type {(event: SubmitEvent) => Promise<void>|undefined}
     */
    #submitListener;

    /**
     * Path of the current page to use when creating history.
     * @type {string}
     */
    #currentUrlForHistory;

    /**
     * Url origin.
     * @type {string}
     */
    #origin;

    /**
     * Initialize pjax by setting up event listeners.
     */
    constructor() {
        super('pjax');

        if (this.dataset.version) {
            this.#version = this.dataset.version;
        }

        this.#popStateListener = event => this.#onPopState(event);
        this.#clickListener = event => this.#onClickListener(event);
        this.#submitListener = event => this.#onSubmitListener(event);

        window.addEventListener('popstate', this.#popStateListener);
        this.addEventListener('click', this.#clickListener);
        this.addEventListener('submit', this.#submitListener);

        // create a state object for the current page with out special properties
        window.history.replaceState({ url: window.location.href, title: document.title }, '');

        const currentUrl = new URL(window.location.href);
        this.#currentUrlForHistory = this.#getUrlForHistory(currentUrl);
        this.#origin = currentUrl.origin;
    }

    /**
     * Clean up when removing component.
     */
    disconnectedCallback() {
        if (this.#popStateListener) {
            window.removeEventListener('popstate', this.#popStateListener);
            this.#popStateListener = undefined;
        }
        if (this.#clickListener) {
            this.removeEventListener('click', this.#clickListener);
            this.#clickListener = undefined;
        }
        if (this.#submitListener) {
            this.removeEventListener('submit', this.#submitListener);
            this.#submitListener = undefined;
        }

        super.disconnectedCallback();
    }

    /**
     * Listens for back/forward navigation events and updates page accordingly.
     * @param {PopStateEvent} event PopState event
     */
    async #onPopState(event) {
        if (event.state !== null) {
            // stop if url is missing from state
            if (!(event.state.url && URL.canParse(event.state.url, this.#origin))) {
                return;
            }

            // If there is a state object, handle it as a page load.
            // history is only designed to work with GET requests.  don't want to try to store/rebuild request body for other methods like POST/PUT
            await this.#requestPage(new URL(event.state.url, this.#origin), HttpMethods.GET, false);
        }
    }

    /**
     * Link click listener.
     * @param {Event} event Click event
     */
    async #onClickListener(event) {
        let target = /** @type {HTMLAnchorElement} */ (event.target);
        if (target.nodeName !== 'A') {
            target = target.closest('a');
        }

        // Ignore clicks unless its a link
        if (target?.nodeName !== 'A') {
            return;
        }

        // Ignore non-primary or modified clicks so browser handles new tabs/windows
        if (
            event.button !== 0
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey
        ) {
            return;
        }

        // Ignore links intended for new context or download
        if ((target.target && target.target !== '_self') || target.hasAttribute('download')) {
            return;
        }

        // Ignore clicks if link has the exclude class
        if (this.#excludeAttribute && target.hasAttribute(this.#excludeAttribute)) {
            return;
        }

        if (!URL.canParse(target.href)) {
            return;
        }

        // Ignore external links
        const url = new URL(target.href);
        if (url.origin !== this.#origin) {
            return;
        }

        // Ignore anchors on the same page
        if (url.pathname === window.location.pathname && url.hash.length > 0) {
            return;
        }

        // Skip link if file type is within ignored types array
        if (this.#ignoreFileTypes.includes(url.pathname.split('.').pop().toLowerCase())) {
            return;
        }

        // Don't fire normal event
        event.preventDefault();

        // Take no action if we are already on requested page
        if (window.location.href === target.href) {
            return;
        }

        let method = target.dataset.pjaxMethod;
        if (!(method && method.toUpperCase() in HttpMethods)) {
            method = HttpMethods.GET;
        }

        await this.#requestPage(url, method, true);
    }

    /**
     * Form submit listener.
     * @param {Event} event Submit event.
     */
    async #onSubmitListener(event) {
        let target = /** @type {HTMLFormElement} */ (event.target);
        if (target.nodeName !== 'FORM') {
            target = target.closest('form');
        }

        // Ignore submit unless its a form
        if (target?.nodeName !== 'FORM') {
            return;
        }

        // Ignore submit if the form has the exclude attribute
        if (this.#excludeAttribute && target.hasAttribute(this.#excludeAttribute)) {
            return;
        }

        if (!URL.canParse(target.action)) {
            return;
        }

        // Ignore submit if the origin doesn't match
        const url = new URL(target.action);
        if (url.origin !== this.#origin) {
            return;
        }

        // Don't fire normal event
        event.preventDefault();

        let method = target.dataset.pjaxMethod;
        if (!(method && method.toUpperCase() in HttpMethods)) {
            method = target.method;
        }
        if (!(method && method.toUpperCase() in HttpMethods)) {
            method = HttpMethods.POST;
        }

        // handle the submission
        await this.#submitForm(url, method, new FormData(target));
    }

    /**
     * Performs request to load content from server and handles the result.
     * @param {URL} url Url to request.
     * @param {string} method Http method to use for request.
     * @param {boolean} updateHistory Update browser history if true.
     */
    async #requestPage(url, method, updateHistory) {
        this.#showLoadingIndicator();

        // keep browser from caching requests by tacking milliseconds to end of url
        url.searchParams.set('_t', `${Date.now()}`);

        try {
            const response = await fetch(url, {
                method,
                signal: AbortSignal.timeout(DefaultTimeout),
                headers: this.#buildRequestHeaders(),
            });

            // Handle 302 redirects - should only happen for auth errors
            if (response.redirected || response.status === 302) {
                window.location.assign(response.url);
                return;
            }

            if (!response.ok) {
                throw new FetchError(`HTTP ${response.status}: ${response.statusText}`);
            }

            await this.#processResponse(url, updateHistory, response);
        } catch (error) {
            await this.#handleResponseError(error);
        } finally {
            this.#hideLoadingIndicator();
        }
    }

    /**
     * Performs form submission to server and handles the result.
     * @param {URL} url Url to request.
     * @param {string} method Http method to use for request.
     * @param {FormData} formData Form element to submit.
     */
    async #submitForm(url, method, formData) {
        this.#showLoadingIndicator();

        try {
            const response = await fetch(url, {
                method,
                signal: AbortSignal.timeout(DefaultTimeout),
                headers: this.#buildRequestHeaders(),
                body: formData,
            });

            // Handle 302 redirects - should only happen for auth errors
            if (response.redirected || response.status === 302) {
                window.location.assign(response.url);
                return;
            }

            if (!response.ok) {
                throw new FetchError(`HTTP ${response.status}: ${response.statusText}`);
            }

            await this.#processResponse(url, true, response);
        } catch (error) {
            await this.#handleResponseError(error);
        } finally {
            this.#hideLoadingIndicator();
        }
    }

    /**
     * Creates headers to use with requests to server.
     * @returns {object} Headers for fetch request
     */
    #buildRequestHeaders() {
        const headers = {};
        headers[HttpHeaders.RequestedWith] = 'XMLHttpRequest';
        headers[HttpHeaders.PJax] = true;
        headers[HttpHeaders.PJaxVersion] = this.#version;
        return headers;
    }

    /**
     * Process a response from the server.
     * @param {URL} requestUrl Requested url.
     * @param {boolean} updateHistory Update browser history if true.
     * @param {Response} response Fetch response.
     */
    async #processResponse(requestUrl, updateHistory, response) {
        const body = await getResponseBody(response);

        if (response.headers.has(HttpHeaders.PJaxRefresh)) {
            window.location.assign(requestUrl);
            return;
        }

        if (!response.ok || isJson(response)) {
            this.#showErrorDialog();
            return;
        }

        this.#updateHistory(requestUrl, updateHistory, response);
        const targetElement = this.#updateTarget(body);
        this.#updateTitle(response);
        this.#updateDescription(response);
        this.#updateCanonical(response);
        this.#updateOgType(response);

        window.scrollTo(0, 0);

        const elementToFocus = /** @type {HTMLElement} */ (targetElement.querySelector('[autofocus]'));
        elementToFocus?.focus();
    }

    /**
     * Updates browser history for a successful response.
     * @param {URL} requestUrl Requested url.
     * @param {boolean} updateHistory Update browser history if true.
     * @param {Response} response Fetch response.
     */
    #updateHistory(requestUrl, updateHistory, response) {
        if (!updateHistory) {
            return;
        }

        const newUrl = response.headers.has(HttpHeaders.PJaxPushUrl)
            ? response.headers.get(HttpHeaders.PJaxPushUrl)
            : this.#getUrlForHistory(requestUrl);

        if (this.#currentUrlForHistory !== newUrl) {
            this.#currentUrlForHistory = newUrl;
            window.history.pushState({ url: this.#currentUrlForHistory, title: document.title }, '', this.#currentUrlForHistory);
        }
    }

    /**
     * Updates the page content.
     * @param {string} body HTML content.
     * @returns {HTMLElement|null} Content target element.
     */
    #updateTarget(body) {
        const targetElement = this.getElement(Elements.Target);
        if (targetElement) {
            if (document.startViewTransition) {
                document.startViewTransition(() => {
                    targetElement.innerHTML = body;
                });
            } else {
                targetElement.innerHTML = body;
            }
        }
        return targetElement;
    }

    /**
     * Updates the document title from the response header.
     * @param {Response} response Fetch response.
     */
    #updateTitle(response) {
        if (!response.headers.has(HttpHeaders.PJaxTitle)) {
            return;
        }

        const title = response.headers.get(HttpHeaders.PJaxTitle);
        if (title) {
            document.title = title;

            let metaOgTitle = document.querySelector('meta[property="og:title"]');
            if (!metaOgTitle) {
                metaOgTitle = document.createElement('meta');
                metaOgTitle.setAttribute('property', 'og:title');
                document.head.appendChild(metaOgTitle);
            }
            metaOgTitle.content = title;
        }
    }

    /**
     * Updates the document description from the response header.
     * @param {Response} response Fetch response.
     */
    #updateDescription(response) {
        if (!response.headers.has(HttpHeaders.PJaxDescription)) {
            return;
        }

        const description = response.headers.get(HttpHeaders.PJaxDescription);
        if (description) {
            let metaDescription = document.querySelector('meta[name="description"]');
            if (!metaDescription) {
                metaDescription = document.createElement('meta');
                metaDescription.name = 'description';
                metaDescription.setAttribute('property', 'og:description');
                document.head.appendChild(metaDescription);
            }
            metaDescription.content = description;
        }
    }

    /**
     * Updates the canonical URL from the response header or current page URL.
     * @param {Response} response Fetch response.
     */
    #updateCanonical(response) {
        let linkCanonical = document.querySelector('link[rel="canonical"]');
        const canonicalOrigin = linkCanonical
            ? new URL(linkCanonical.href).origin
            : window.location.origin;
        const canonical = response.headers.has(HttpHeaders.PJaxCanonical)
            ? response.headers.get(HttpHeaders.PJaxCanonical)
            : window.location.pathname;
        if (!canonical) {
            return;
        }

        const canonicalUrl = new URL(canonical, canonicalOrigin);
        if (!linkCanonical) {
            linkCanonical = document.createElement('link');
            linkCanonical.rel = 'canonical';
            document.head.appendChild(linkCanonical);
        }
        linkCanonical.href = canonicalUrl.href;

        let metaOgUrl = document.querySelector('meta[property="og:url"]');
        if (!metaOgUrl) {
            metaOgUrl = document.createElement('meta');
            metaOgUrl.setAttribute('property', 'og:url');
            document.head.appendChild(metaOgUrl);
        }
        metaOgUrl.content = canonicalUrl.href;
    }

    /**
     * Updates the Open Graph type from the response header.
     * @param {Response} response Fetch response.
     */
    #updateOgType(response) {
        const ogType = response.headers.has(HttpHeaders.PJaxOgType)
            ? response.headers.get(HttpHeaders.PJaxOgType)
            : 'website';
        if (ogType) {
            let metaOgType = document.querySelector('meta[property="og:type"]');
            if (!metaOgType) {
                metaOgType = document.createElement('meta');
                metaOgType.setAttribute('property', 'og:type');
                document.head.appendChild(metaOgType);
            }
            metaOgType.content = ogType;
        }
    }

    /**
     * Builds a URL string for history, excluding the cache-busting _t parameter.
     * @param {URL} url URL to build from.
     * @returns {string} URL string with pathname and search params (excluding _t).
     */
    #getUrlForHistory(url) {
        const params = new URLSearchParams(url.searchParams);
        params.delete('_t');
        const search = params.toString();
        return search ? `${url.pathname}?${search}` : url.pathname;
    }

    /**
     * Shows the loading indicator.
     */
    #showLoadingIndicator() {
        this.getElement(Elements.LoadingIndicator)?.classList.add('pjax-request');
    }

    /**
     * Hides the loading indicator.
     */
    #hideLoadingIndicator() {
        this.getElement(Elements.LoadingIndicator)?.classList.remove('pjax-request');
    }

    /**
     * Handle response errors.
     * @param {FetchError} error Error from fetch.
     */
    async #handleResponseError(error) {
        console.error(error.message);
        this.#showErrorDialog();
    }

    /**
     * Shows the error dialog.
     */
    #showErrorDialog() {
        const element = /** @type {NillaInfo} */ (this.getElement(Elements.InfoDialog));
        element?.show();
    }
}

// Define the new web component
if ('customElements' in window) {
    customElements.define('nilla-pjax', PJax);
}

export default PJax;
